// The cast: every character a bot seat can be, one data file each
// (AI_MANAGERS.md §4.6, BACKLOG D9).
//
// CHARACTERS ARE DATA; COMPETENCE IS CODE. A bot is a file in `../data`: its
// name, its voice, its team's name, its skill band, its five dials, and
// what it says on each occasion (`talk.ts`). How a dial becomes a move is
// `style.ts` and the planners beside it, and no character has code of its
// own. Adding one is a file and its line in `FILES` below; the cast's test
// refuses a file in the folder that is not listed, a file that does not
// parse, two characters with one name, and a line holding the words the
// notifications are kept free of — start, bench, drop, pick up — or "you
// should" (§6.2).
//
// THE ORDER IS THE ORDER THEY JOINED. `aiForSeat` hands seats out in it, and
// the season suite drafts with the first eight, so a new character goes at
// the end and moves nobody's recorded season.
//
// PARSED WHEN THIS LOADS, and a bad file throws: a cast that cannot be read
// is a build that cannot run, which the gates catch before anybody seats one.

import { z } from 'zod';
import { SKILL_BANDS } from './skill';
import uncleRico from '../data/uncle-rico.json';
import theAccountant from '../data/the-accountant.json';
import chaosGremlin from '../data/chaos-gremlin.json';
import coachBot from '../data/coach-bot.json';
import waiverWendy from '../data/waiver-wendy.json';
import sundaySam from '../data/sunday-sam.json';
import theProfessor from '../data/the-professor.json';
import luckyLou from '../data/lucky-lou.json';
import grandmaDot from '../data/grandma-dot.json';
import captainHype from '../data/captain-hype.json';
import midnightMike from '../data/midnight-mike.json';
import theIntern from '../data/the-intern.json';

/**
 * Every moment a bot has something to say about, in a draft or a season
 * (§6.1). Each is a list in every file, and `SLOTS` in `talk.ts` says which
 * words a line for it may use.
 */
export const OCCASIONS = [
  'pick',
  'draftDone',
  'winClose',
  'win',
  'winBig',
  'lossClose',
  'loss',
  'lossBig',
  'waiverWon',
  'tradeOffer',
  'tradeDone',
  'champion',
  'eliminated',
  'preview',
  'leading',
  'trailing',
] as const;
export type Occasion = (typeof OCCASIONS)[number];

const Dial = z.number().min(0).max(1);
/** A line, before its words are filled: well inside a chat message's 300. */
const Line = z.string().min(1).max(160);
/** At least three a moment, so a bot does not say the same thing every time. */
const Lines = z.array(Line).min(3);

// HOW IT DRAFTS (AI_MANAGERS.md §4.7): a plan for building its roster, its
// tastes in players, how it reacts to the room. A strength is in PLACES: how
// many places down the list the taste reaches for a player. Where a part names
// a position or a club, it is under the sport's own key, because the core
// names no sport; a league of a sport the file says nothing about drafts by
// the tastes and the room alone.
const Places = z.number().min(0).max(20);
const Position = z.string().min(1).max(8);
const Club = z.string().min(1).max(8);
const SportPlan = z
  .object({
    /** The positions it fills its starters in, first first. */
    order: z.array(Position).min(2).max(6).optional(),
    /** At least `atLeast` at a position by its pick in `byRound`. */
    targets: z
      .array(
        z
          .object({
            position: Position,
            atLeast: z.number().int().min(1).max(4),
            byRound: z.number().int().min(1).max(12),
            strength: Places.optional(),
          })
          .strict(),
      )
      .max(4)
      .optional(),
    /** At most `atMost` at a position through round `throughRound`. */
    limits: z
      .array(
        z
          .object({
            position: Position,
            atMost: z.number().int().min(0).max(3),
            throughRound: z.number().int().min(1).max(12),
          })
          .strict(),
      )
      .max(4)
      .optional(),
    /** A position it keeps taking past its starters. */
    hoard: Position.optional(),
    /** A position whose backups it takes from its own players' clubs. */
    handcuff: Position.optional(),
    /** Its `with` player's own club mates at `positions`. */
    stack: z
      .object({
        with: Position,
        positions: z.array(Position).min(1).max(3),
        strength: Places.optional(),
      })
      .strict()
      .optional(),
    /** Clubs it loves or will not draft from whatever the league, ahead of any drawn. */
    loves: z.array(Club).max(3).optional(),
    hates: z.array(Club).max(3).optional(),
  })
  .strict();
const DraftStyleFile = z
  .object({
    build: z.enum(['value', 'starters-first']),
    /** How many rounds sooner than the habit it lets a late position in. */
    lateEarly: z.number().int().min(0).max(5).optional(),
    tastes: z
      .object({
        /** How many clubs it loves, its sport's named ones first, the rest drawn per league. */
        loves: z
          .object({ count: z.number().int().min(1).max(3), strength: Places })
          .strict()
          .optional(),
        hates: z
          .object({ count: z.number().int().min(1).max(3) })
          .strict()
          .optional(),
        /** It will not draft its loved clubs' division rivals either. */
        division: z.boolean().optional(),
        rookies: z.enum(['love', 'avoid']).optional(),
        veterans: z.boolean().optional(),
        injured: z.enum(['avoid', 'bargain']).optional(),
        ironMan: z.boolean().optional(),
        /** First names it has a soft spot for. */
        namesake: z.array(z.string().min(2).max(20)).max(4).optional(),
        byes: z.enum(['spread', 'stack']).optional(),
      })
      .strict(),
    room: z
      .object({
        runs: z.enum(['follow', 'against']).optional(),
        forecast: z.boolean().optional(),
        hawk: z.boolean().optional(),
        /** How far it reaches for its own guys; a little for anybody. */
        reach: Places.optional(),
        tilt: z.boolean().optional(),
        copycat: z.boolean().optional(),
        panic: z.boolean().optional(),
        /** In an auction, it puts up players it does not want, to spend the room's money. */
        drain: z.boolean().optional(),
      })
      .strict(),
    sports: z.record(z.string(), SportPlan),
  })
  .strict();
export type DraftStyleFile = z.infer<typeof DraftStyleFile>;

export const BotFile = z
  .object({
    /** Stable for good: stored on every seat the bot was given (`presetId`). */
    id: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    name: z.string().min(2).max(24),
    /** One line in its own voice, shown where it is chosen. */
    voice: z.string().min(1).max(80),
    /** How it plays, in a sentence or two, for whoever writes or chooses it. */
    plays: z.string().min(1).max(240),
    /** What a seat it takes at the draft is called (B-125). */
    teamName: z.string().min(2).max(40),
    /**
     * Its team's short name, chosen rather than worked out: the first three
     * letters of three of the cast's teams are THE, and the initials of
     * another spell something nobody wants in a standings table.
     */
    abbreviation: z.string().regex(/^[A-Z0-9]{2,4}$/),
    /** How well it plays by default (§4.2): a seat may be given another band. */
    skill: z.enum(SKILL_BANDS),
    dials: z
      .object({ trashTalk: Dial, aggression: Dial, risk: Dial, loyalty: Dial, activity: Dial })
      .strict(),
    /** How it drafts (§4.7). */
    draft: DraftStyleFile,
    lines: z
      .object({
        pick: Lines,
        draftDone: Lines,
        winClose: Lines,
        win: Lines,
        winBig: Lines,
        lossClose: Lines,
        loss: Lines,
        lossBig: Lines,
        waiverWon: Lines,
        tradeOffer: Lines,
        tradeDone: Lines,
        champion: Lines,
        eliminated: Lines,
        /** The matchup's line (§6.5): before any of the week's games. */
        preview: Lines,
        /** And while the week is played, ahead or behind. */
        leading: Lines,
        trailing: Lines,
      })
      .strict(),
  })
  .strict();
export type CastMember = z.infer<typeof BotFile>;

/** The files, in the order the characters joined the cast. */
export const FILES: readonly unknown[] = [
  uncleRico,
  theAccountant,
  chaosGremlin,
  coachBot,
  waiverWendy,
  sundaySam,
  theProfessor,
  luckyLou,
  grandmaDot,
  captainHype,
  midnightMike,
  theIntern,
];

export const CAST: readonly CastMember[] = FILES.map((file, i) => {
  const parsed = BotFile.safeParse(file);
  if (!parsed.success)
    throw new Error(
      `bot file ${String(i + 1)} in the cast cannot be read: ${parsed.error.message}`,
    );
  return parsed.data;
});

/** A character by its id, or null for a seat no file describes. */
export function castMember(id: string | null | undefined): CastMember | null {
  return CAST.find((c) => c.id === id) ?? null;
}
